import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchHotels } from '../services/api';
import Papa from 'papaparse';

// Helper to get date 1 month from now as ISO string
const getDefaultDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() + 1);
  return date.toISOString().split('T')[0];  // Returns YYYY-MM-DD format
};

const initialState = {
  date: getDefaultDate(),
  destination: '',
  departureAirport: '',
  duration: 7,
  results: [],
  destinations: [],
  airports: [],
  pagination: {
    currentPage: 1,
    totalPages: 0,
    totalResults: 0
  },
  preferences: {
    perPage: 10,
    sortBy: 'default'
  },
  loading: false,
  error: null
};

export const performSearch = createAsyncThunk(
  'search/performSearch',
  async ({ page, limit, sortBy }, { getState, dispatch }) => {
    const state = getState();
    // Create searchParams object from individual state values
    const searchParams = {
      date: state.search.date,
      destination: state.search.destination,
      departureAirport: state.search.departureAirport,
      duration: state.search.duration
    };

    try {
      // Always request maximum results from API (100)
      // Pass airports data from Redux store for region expansion
      const result = await fetchHotels(1, 100, sortBy, searchParams, state.search.airports);
      
      return {
        ...result,
        // Keep original pagination values for total count
        pagination: {
          currentPage: page,
          totalPages: Math.ceil(result.hotels.length / limit),
          totalResults: result.hotels.length
        }
      };
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error.message 
      });
      throw error;
    }
  }
);

export const loadDestinations = createAsyncThunk(
  'search/loadDestinations',
  async () => {
    const response = await fetch('/data/locations.csv');
    const csv = await response.text();
    
    return new Promise((resolve, reject) => {
      Papa.parse(csv, {
        header: true,
        complete: (results) => {
          const destinations = results.data.map(row => ({
            country: row.Holidaycountry,
            region: row.Holidayregion,
            resort: row.Holidayresort,
            code: `${row.Holidayresort}-${row.Holidayregion}-${row.Holidaycountry}`.replace(/\s+/g, '-').toLowerCase()
          }));
          resolve(destinations);
        },
        error: (error) => reject(error)
      });
    });
  }
);

export const loadAirports = createAsyncThunk(
  'search/loadAirports',
  async () => {
    const response = await fetch('/data/departure_airports.csv');
    const csv = await response.text();
    
    return new Promise((resolve, reject) => {
      Papa.parse(csv, {
        header: false,
        complete: (results) => {
          const airports = [];
          let currentGroup = null;
          let currentGroupAirports = [];
          let regionOrder = 0; // Track the order of regions
          
          results.data.forEach(row => {
            if (!row[0] || !row[1]) return; // Skip empty rows
            
            const [name, code, isRegion] = row;
            
            if (isRegion === '1') {
              // If we have a previous group, save its airports
              if (currentGroup) {
                currentGroup.childAirports = currentGroupAirports;
                airports.push(currentGroup);
              }
              
              // Start new group
              currentGroup = {
                id: code,
                name: name,
                code: code,
                isRegion: true,
                childAirports: [],
                order: regionOrder++ // Add order property
              };
              currentGroupAirports = [];
            } else {
              // Regular airport
              const airport = {
                id: code,
                name: name,
                code: code,
                isRegion: false,
                parentRegion: currentGroup?.code
              };
              airports.push(airport);
              currentGroupAirports.push(airport);
            }
          });
          
          // Don't forget to add the last group
          if (currentGroup) {
            currentGroup.childAirports = currentGroupAirports;
            airports.push(currentGroup);
          }

          // Sort: regions first by their original order, then airports alphabetically
          const sortedAirports = airports.sort((a, b) => {
            if (a.isRegion && b.isRegion) {
              return a.order - b.order; // Sort regions by their original order
            }
            if (a.isRegion) return -1;
            if (b.isRegion) return 1;
            return a.name.localeCompare(b.name);
          });

          resolve(sortedAirports);
        },
        error: (error) => reject(error)
      });
    });
  }
);

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setSearchResults: (state, action) => {
      state.results = action.payload.deals || [];
      state.loading = false;
      state.error = null;
    },
    setSearchParams: (state, action) => {
      // Update search parameters
      Object.keys(action.payload).forEach(key => {
        state[key] = action.payload[key];
      });
    },
    setPreferences: (state, action) => {
      state.preferences = {
        ...state.preferences,
        ...action.payload
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(performSearch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(performSearch.fulfilled, (state, action) => {
        state.loading = false;
        state.results = action.payload.hotels;
        state.pagination = action.payload.pagination;
      })
      .addCase(performSearch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(loadDestinations.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadDestinations.fulfilled, (state, action) => {
        state.destinations = action.payload;
        state.loading = false;
      })
      .addCase(loadDestinations.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(loadAirports.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadAirports.fulfilled, (state, action) => {
        state.airports = action.payload;
        state.loading = false;
      })
      .addCase(loadAirports.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      });
  }
});

export const { setSearchParams, setPreferences } = searchSlice.actions;
export default searchSlice.reducer;